import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useRef, useState } from 'react';
import { Box, Button, Input, Stack, Text } from '@chakra-ui/react';
import { TRPCClientError } from '@trpc/client';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { NETWORK_NAME_UNIQUE_VIOLATION_ERROR } from '@/services/constraints/errorMessages';
import { isValidNetworkVersion } from '@/services/models/networkVersion';
import { NetworkExportErrors } from '@/utils/network/release/getReleaseErrorsForAVariable';
import { formatSubjectNames, groupReleaseErrors } from '@/utils/network/release/releaseErrorSummary';
import { ReleaseValidationError } from '@/utils/network/release/releaseValidationError';
export type ReleaseDialogProps = {
  isOpen: boolean;
  networkName: string;
  onClose: () => void;
  onSubmit: (version: string) => Promise<void>;
};
function ReleaseErrorSummary(t0) {
  const $ = _c(38);
  const {
    errors
  } = t0;
  let T0;
  let T1;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== errors) {
    const groups = groupReleaseErrors(errors);
    T1 = Box;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        bg: "red.900",
        borderColor: "red.700"
      };
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t6 = "release-validation-errors";
    t7 = "red.50";
    t8 = "red.200";
    t9 = "md";
    t10 = "1px";
    t11 = 4;
    t12 = 3;
    const t14 = errors.length === 1 ? "1 problem blocks this release" : `${errors.length} problems block this release`;
    if ($[17] !== t14) {
      t13 = <Text fontSize="sm" fontWeight="semibold" mb={2}>{t14}</Text>;
      $[17] = t14;
      $[18] = t13;
    } else {
      t13 = $[18];
    }
    T0 = Stack;
    t1 = 2;
    t2 = "12rem";
    t3 = "auto";
    t4 = groups.map(_temp);
    $[0] = errors;
    $[1] = T0;
    $[2] = T1;
    $[3] = t1;
    $[4] = t10;
    $[5] = t11;
    $[6] = t12;
    $[7] = t13;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[1];
    T1 = $[2];
    t1 = $[3];
    t10 = $[4];
    t11 = $[5];
    t12 = $[6];
    t13 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t14;
  if ($[19] !== T0 || $[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t14 = <T0 gap={t1} maxH={t2} overflowY={t3}>{t4}</T0>;
    $[19] = T0;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Text _dark={{
      color: "gray.300"
    }} color="gray.600" fontSize="xs" mt={2}>The full report was downloaded as a JSON file.</Text>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== T1 || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t16 = <T1 _dark={t5} aria-label={t6} bg={t7} borderColor={t8} borderRadius={t9} borderWidth={t10} mb={t11} p={t12}>{t13}{t14}{t15}</T1>;
    $[26] = T1;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}

// Releasing stamps an immutable version (KBE-544). For now the release
// is named after its version — there's no separate free-text name field
// — so the version doubles as the new network's (globally unique) name.
function _temp(group) {
  return <Box key={group.label}><Text fontSize="sm" fontWeight="medium">{group.label} ({group.count})</Text><Text _dark={{
      color: "gray.300"
    }} color="gray.600" fontSize="xs">{formatSubjectNames(group.subjectNames)}</Text></Box>;
}
const ReleaseDialog = forwardRef<HTMLDivElement, ReleaseDialogProps>((t0, ref) => {
  const $ = _c(51);
  const {
    isOpen,
    networkName,
    onClose,
    onSubmit
  } = t0;
  const versionInputRef = useRef(null);
  const [version, setVersion] = useState("");
  const [versionAlreadyExists, setVersionAlreadyExists] = useState(false);
  const [releaseErrors, setReleaseErrors] = useState(null);
  let t1;
  if ($[0] !== version) {
    t1 = isValidNetworkVersion(version);
    $[0] = version;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const versionValid = t1;
  const canSubmit = versionValid && !versionAlreadyExists && releaseErrors === null;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setVersion("");
      setVersionAlreadyExists(false);
      setReleaseErrors(null);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const reset = t2;
  let t3;
  if ($[3] !== onClose) {
    t3 = () => {
      onClose();
      reset();
    };
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleClose = t3;
  let t4;
  if ($[5] !== canSubmit || $[6] !== handleClose || $[7] !== onSubmit || $[8] !== version) {
    t4 = async () => {
      if (!canSubmit) {
        return;
      }
      ;
      try {
        await onSubmit(version.trim());
        handleClose();
      } catch (t5) {
        const error = t5;
        if (error instanceof TRPCClientError && error.message === NETWORK_NAME_UNIQUE_VIOLATION_ERROR) {
          setVersionAlreadyExists(true);
        }
        if (error instanceof ReleaseValidationError) {
          setReleaseErrors(error.errors);
        }
      }
    };
    $[5] = canSubmit;
    $[6] = handleClose;
    $[7] = onSubmit;
    $[8] = version;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleSubmit = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => versionInputRef.current;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== handleClose) {
    t6 = e => {
      if (!e.open) {
        handleClose();
      }
    };
    $[11] = handleClose;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DialogHeader><DialogTitle>Release network</DialogTitle></DialogHeader>;
    t8 = <DialogCloseTrigger />;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== networkName) {
    t9 = <Text color="gray.500" fontSize="sm" mb={3}>Releasing {networkName}</Text>;
    $[15] = networkName;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== releaseErrors) {
    t10 = releaseErrors !== null && <ReleaseErrorSummary errors={releaseErrors} />;
    $[17] = releaseErrors;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = versionAlreadyExists ? "This version already exists" : "Use major.minor.patch, e.g. 3.23.0";
  const t12 = versionAlreadyExists || version !== "" && !versionValid;
  let t13;
  if ($[19] !== releaseErrors || $[20] !== versionAlreadyExists) {
    t13 = e_0 => {
      setVersion(e_0.currentTarget.value);
      if (versionAlreadyExists) {
        setVersionAlreadyExists(false);
      }
      if (releaseErrors !== null) {
        setReleaseErrors(null);
      }
    };
    $[19] = releaseErrors;
    $[20] = versionAlreadyExists;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== canSubmit || $[23] !== handleSubmit) {
    t14 = e_1 => {
      if (e_1.key === "Enter" && canSubmit) {
        handleSubmit();
      }
    };
    $[22] = canSubmit;
    $[23] = handleSubmit;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t13 || $[26] !== t14 || $[27] !== version) {
    t15 = <Input ref={versionInputRef} placeholder="3.23.0" value={version} onChange={t13} onKeyDown={t14} />;
    $[25] = t13;
    $[26] = t14;
    $[27] = version;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t15) {
    t16 = <Field errorText={t11} invalid={t12} label="Version (required)">{t15}</Field>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Text color="gray.500" fontSize="sm" mt={2}>The version is stamped on the release and cannot be changed later.</Text>;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t10 || $[35] !== t16 || $[36] !== t9) {
    t18 = <DialogBody>{t9}{t10}{t16}{t17}</DialogBody>;
    $[34] = t10;
    $[35] = t16;
    $[36] = t9;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = !canSubmit;
  let t20;
  if ($[38] !== handleSubmit) {
    t20 = () => void handleSubmit();
    $[38] = handleSubmit;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <DialogFooter><Button disabled={t19} onClick={t20}>Release</Button></DialogFooter>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== ref || $[44] !== t18 || $[45] !== t21) {
    t22 = <DialogContent ref={ref}>{t7}{t8}{t18}{t21}</DialogContent>;
    $[43] = ref;
    $[44] = t18;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== isOpen || $[48] !== t22 || $[49] !== t6) {
    t23 = <DialogRoot initialFocusEl={t5} open={isOpen} onOpenChange={t6}>{t22}</DialogRoot>;
    $[47] = isOpen;
    $[48] = t22;
    $[49] = t6;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
});
ReleaseDialog.displayName = 'ReleaseDialog';
export default ReleaseDialog;